import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import { ArrowBack } from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import EmailField from '@/components/ui/EmailField/EmailField';
import Grid from '@/components/ui/Grid/Grid';
import IconButton from '@/components/ui/IconButton/IconButton';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton';
import Typography from '@/components/ui/Typography/Typography';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { ForgotPasswordFormValues } from './IForgotPasswordPage';
import { styles } from './styles';

const initialValues: ForgotPasswordFormValues = { email: '' };

const validationSchema = yup.object().shape({
    email: yup.string().email('invalid email').required('required'),
});

const ForgotPasswordPage = () => {
    const [loading, setLoading] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);

    const auth = useAuth();
    const navigate = useNavigate();

    const onResetSuccess = (email: string) => {
        setLoading(false);
        navigate('/login/password_reset_email_sent', { state: { email } });
    };

    const onResetError = (_err: unknown, email: string) => {
        setLoading(false);
        navigate('/login/password_reset_email_sent', { state: { email } });
    };

    const handleSubmit = ({ email }: ForgotPasswordFormValues) => {
        setLoading(true);
        void auth.actions.resetPassword(email, onResetSuccess, onResetError);
    };

    const handlePreventDefault = (e: MouseEvent<HTMLButtonElement>) => e.preventDefault();

    const isReadyToSubmit = (errors: { email?: string }) =>
        emailTouched && !errors.email;

    return (
        <Grid container item alignItems="center" justifyContent="center" gap="44px">
            <Grid container item direction="column" alignItems="center" justifyContent="center">
                <Grid container item alignItems="center" justifyContent="space-between">
                    <Grid item xs={1}>
                        <IconButton
                            aria-label="back"
                            onClick={() => navigate(-1)}
                            onMouseDown={handlePreventDefault}
                            onMouseUp={handlePreventDefault}
                            edge="end"
                            disabled={loading}
                        >
                            <ArrowBack style={styles.backButton} fontSize="small" />
                        </IconButton>
                    </Grid>
                    <Grid item xs>
                        <Typography align="center" variant="h3" sx={styles.logoText}>
                            Forgot Password
                        </Typography>
                    </Grid>
                    <Grid item xs={1} />
                </Grid>
                <span role="img" aria-label="locked" style={styles.lockedImg}>
                    {String.fromCodePoint(0x1f512)}
                </span>
            </Grid>
            <Formik
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnChange
                validateOnBlur={false}
            >
                {({ values, errors, touched, handleBlur, handleChange, handleSubmit: submit }) => (
                    <form onSubmit={submit} style={{ minWidth: '100%' }}>
                        <Box sx={styles.formStack}>
                            <Typography align="center" variant="body1">
                                Confirm your email and we will send you instructions to reset your password.
                            </Typography>
                            <EmailField
                                id="outlined-email"
                                name="email"
                                label="Enter Email"
                                value={values.email}
                                error={!!touched.email && !!errors.email}
                                helperText={touched.email ? errors.email : ''}
                                disabled={loading}
                                onChange={(e) => {
                                    setEmailTouched(true);
                                    handleChange(e);
                                }}
                                onBlur={handleBlur}
                            />
                            <LoadingButton
                                type="submit"
                                color="secondary"
                                variant={isReadyToSubmit(errors) ? 'contained' : 'outlined'}
                                loading={loading}
                                fullWidth
                            >
                                SEND RESET EMAIL
                            </LoadingButton>
                        </Box>
                    </form>
                )}
            </Formik>
        </Grid>
    );
};

export default ForgotPasswordPage;
